import { useRef, useMemo, useState } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { POLAROID_CONFIG } from '../../utils/constants'
import { getWeightedProgress } from '../../utils/positions'
import { useTreeStore } from '../../store/useTreeStore'

interface PolaroidsProps {
  progress: number
  galleryProgress?: number // 画廊模式进度
}

interface PolaroidData {
  targetPosition: THREE.Vector3
  chaosPosition: THREE.Vector3
  rotation: THREE.Euler
  imageIndex: number
}

// 图片占位符配置 - 用户可以在这里替换为自己的照片路径
// 将照片放入 public/photos/ 文件夹，然后在这里添加路径
export const PHOTO_PLACEHOLDERS = [
  '/photos/photo1.jpg',   // 占位符1 - 替换为你的照片
  '/photos/photo2.jpg',   // 占位符2 - 替换为你的照片
  '/photos/photo3.jpg',   // 占位符3 - 替换为你的照片
  '/photos/photo4.jpg',   // 占位符4 - 替换为你的照片
  '/photos/photo5.jpg',   // 占位符5 - 替换为你的照片
  '/photos/photo6.jpg',   // 占位符6 - 替换为你的照片
  '/photos/photo7.jpg',   // 占位符7 - 替换为你的照片
  '/photos/photo8.jpg',   // 占位符8 - 替换为你的照片
  '/photos/photo9.jpg',   // 占位符9 - 替换为你的照片
  '/photos/photo10.jpg',  // 占位符10 - 替换为你的照片
  '/photos/photo11.jpg',  // 占位符11 - 替换为你的照片
  '/photos/photo12.jpg',  // 占位符12 - 替换为你的照片
  '/photos/photo13.jpg',  // 占位符13 - 替换为你的照片
  '/photos/photo14.jpg',  // 占位符14 - 替换为你的照片
  '/photos/photo15.jpg',  // 占位符15 - 替换为你的照片
  '/photos/photo16.jpg',  // 占位符16 - 替换为你的照片
  '/photos/photo17.jpg',  // 占位符17 - 替换为你的照片
  '/photos/photo18.jpg',  // 占位符18 - 替换为你的照片
  '/photos/photo19.jpg',  // 占位符19 - 替换为你的照片
  '/photos/photo20.jpg',  // 占位符20 - 替换为你的照片
]

// 默认占位符颜色（当照片未加载时显示）- 扩展到20种颜色
const PLACEHOLDER_COLORS = [
  '#ffcdd2', // 粉红
  '#c8e6c9', // 浅绿
  '#bbdefb', // 浅蓝
  '#fff9c4', // 浅黄
  '#e1bee7', // 浅紫
  '#ffe0b2', // 浅橙
  '#b2dfdb', // 浅青
  '#f8bbd9', // 浅粉
  '#d1c4e9', // 淡紫
  '#b3e5fc', // 天蓝
  '#dcedc8', // 草绿
  '#ffccbc', // 桃红
  '#cfd8dc', // 银灰
  '#f0f4c3', // 柠檬黄
  '#b2ebf2', // 青绿
  '#ffecb3', // 金黄
  '#d7ccc8', // 米色
  '#c5cae9', // 薰衣草
  '#ffcc80', // 橙黄
  '#a5d6a7', // 薄荷绿
]

// 单个拍立得照片组件
function PolaroidPhoto({
  position,
  rotation,
  progress,
  galleryProgress,
  galleryPosition,
  imageIndex,
  texture,
  isFocusPhoto,
  renderOrder = 0,
  onPhotoClick
}: {
  position: THREE.Vector3
  rotation: THREE.Euler
  progress: number
  galleryProgress: number
  galleryPosition: THREE.Vector3
  imageIndex: number
  texture: THREE.Texture | null
  isFocusPhoto: boolean
  renderOrder?: number
  onPhotoClick?: (imageIndex: number) => void
}) {
  const groupRef = useRef<THREE.Group>(null)
  
  // 用于丝滑动画的状态
  const currentPosRef = useRef(new THREE.Vector3().copy(position))
  const currentRotRef = useRef(new THREE.Euler().copy(rotation))
  const currentScaleRef = useRef(1)
  const currentOpacityRef = useRef(1)
  const [animatedOpacity, setAnimatedOpacity] = useState(1)
  
  // 计算目标值
  const chaosScale = 1.2
  const formedScale = 0.8
  const baseScale = chaosScale + (formedScale - chaosScale) * progress
  const galleryScale = isFocusPhoto ? 2.2 : 0.8
  const targetScale = baseScale * (1 - galleryProgress) + galleryScale * galleryProgress
  const targetOpacity = isFocusPhoto ? 1.0 : (1.0 - galleryProgress * 0.7)
  
  // 计算目标位置
  const targetPosition = useMemo(() => {
    if (galleryProgress <= 0) return position
    return new THREE.Vector3().lerpVectors(position, galleryPosition, galleryProgress)
  }, [position, galleryPosition, galleryProgress])
  
  // 计算目标旋转
  const targetRotation = useMemo(() => {
    if (galleryProgress <= 0) return rotation
    const galleryRotation = new THREE.Euler(0, 0, 0)
    return new THREE.Euler(
      rotation.x * (1 - galleryProgress) + galleryRotation.x * galleryProgress,
      rotation.y * (1 - galleryProgress) + galleryRotation.y * galleryProgress,
      rotation.z * (1 - galleryProgress) + galleryRotation.z * galleryProgress
    )
  }, [rotation, galleryProgress])
  
  // 使用 useFrame 实现丝滑动画
  useFrame((state) => {
    if (!groupRef.current) return
    
    // 丝滑插值位置（0.08 = 较快的过渡速度）
    const lerpFactor = 0.08
    currentPosRef.current.lerp(targetPosition, lerpFactor)
    groupRef.current.position.copy(currentPosRef.current)
    
    // 丝滑插值旋转
    currentRotRef.current.x += (targetRotation.x - currentRotRef.current.x) * lerpFactor
    currentRotRef.current.y += (targetRotation.y - currentRotRef.current.y) * lerpFactor
    currentRotRef.current.z += (targetRotation.z - currentRotRef.current.z) * lerpFactor
    
    // 添加轻微摆动（仅在非画廊模式）
    if (progress > 0.5 && galleryProgress < 0.5) {
      const swing = Math.sin(state.clock.elapsedTime * 2 + imageIndex) * 0.05
      groupRef.current.rotation.set(
        currentRotRef.current.x,
        currentRotRef.current.y,
        currentRotRef.current.z + swing * progress
      )
    } else {
      groupRef.current.rotation.set(
        currentRotRef.current.x,
        currentRotRef.current.y,
        currentRotRef.current.z
      )
    }
    
    // 丝滑插值缩放
    currentScaleRef.current += (targetScale - currentScaleRef.current) * lerpFactor
    groupRef.current.scale.setScalar(currentScaleRef.current)
    
    // 丝滑插值透明度
    currentOpacityRef.current += (targetOpacity - currentOpacityRef.current) * lerpFactor
    // 只在变化较大时更新状态，避免频繁重渲染
    if (Math.abs(currentOpacityRef.current - animatedOpacity) > 0.01) {
      setAnimatedOpacity(currentOpacityRef.current)
    }
  })
  
  // 画廊模式下禁用深度测试，让 renderOrder 生效
  const disableDepth = galleryProgress > 0.5
  
  // 处理点击事件
  const handleClick = (event: { stopPropagation: () => void }) => {
    // 阻止事件冒泡
    event.stopPropagation()
    
    // 只有在展开模式（progress < 0.5）且不在画廊模式时才响应点击
    if (progress < 0.5 && galleryProgress < 0.1 && onPhotoClick) {
      onPhotoClick(imageIndex)
    }
  }
  
  return (
    <group
      ref={groupRef}
      renderOrder={renderOrder}
      onClick={handleClick}
      onPointerOver={() => {
        // 展开模式下显示手型光标
        if (progress < 0.5 && galleryProgress < 0.1) {
          document.body.style.cursor = 'pointer'
        }
      }}
      onPointerOut={() => {
        document.body.style.cursor = 'auto'
      }}
    >
      {/* 白色边框 - 增加发光效果 */}
      <mesh renderOrder={renderOrder}>
        <boxGeometry args={[
          POLAROID_CONFIG.width + POLAROID_CONFIG.borderWidth * 2,
          POLAROID_CONFIG.height + POLAROID_CONFIG.borderWidth + POLAROID_CONFIG.bottomBorder,
          0.02
        ]} />
        <meshStandardMaterial
          color="#ffffff"
          roughness={0.2}
          metalness={0.1}
          emissive="#ffffff"
          emissiveIntensity={0.1 * progress}
          transparent
          opacity={animatedOpacity}
          depthTest={!disableDepth}
          depthWrite={!disableDepth}
        />
      </mesh>
      
      {/* 照片区域 - 如果有纹理则显示照片，否则显示占位符颜色 */}
      <mesh position={[0, POLAROID_CONFIG.bottomBorder / 2 - POLAROID_CONFIG.borderWidth / 2, 0.015]} renderOrder={renderOrder + 1}>
        <planeGeometry args={[POLAROID_CONFIG.width, POLAROID_CONFIG.height - POLAROID_CONFIG.bottomBorder]} />
        {texture ? (
          <meshBasicMaterial
            map={texture}
            transparent
            opacity={animatedOpacity}
            depthTest={!disableDepth}
            depthWrite={!disableDepth}
          />
        ) : (
          <meshBasicMaterial
            color={PLACEHOLDER_COLORS[imageIndex % PLACEHOLDER_COLORS.length]}
            transparent
            opacity={animatedOpacity}
            depthTest={!disableDepth}
            depthWrite={!disableDepth}
          />
        )}
      </mesh>
      
      {/* 照片装饰 - 爱心图标（仅在没有照片时显示） */}
      {!texture && (
        <mesh position={[0, 0, 0.02]} renderOrder={renderOrder + 2}>
          <circleGeometry args={[0.15, 32]} />
          <meshBasicMaterial
            color="#ff6b6b"
            transparent
            opacity={animatedOpacity * 0.8}
            depthTest={!disableDepth}
            depthWrite={!disableDepth}
          />
        </mesh>
      )}
      
      {/* 占位符文字提示 - 仅在没有照片时显示 */}
      {!texture && (
        <mesh position={[0, -0.3, 0.02]} renderOrder={renderOrder + 2}>
          <planeGeometry args={[0.8, 0.2]} />
          <meshBasicMaterial
            color="#666666"
            transparent
            opacity={animatedOpacity * 0.5}
            depthTest={!disableDepth}
            depthWrite={!disableDepth}
          />
        </mesh>
      )}
      
      {/* 挂绳 - 画廊模式下隐藏 */}
      {galleryProgress < 0.5 && (
        <mesh position={[0, (POLAROID_CONFIG.height + POLAROID_CONFIG.bottomBorder) / 2 + 0.1, 0]}>
          <cylinderGeometry args={[0.01, 0.01, 0.3, 8]} />
          <meshStandardMaterial
            color="#8b4513"
            roughness={0.8}
            transparent
            opacity={animatedOpacity}
          />
        </mesh>
      )}
    </group>
  )
}

// 拍立得照片区域的目标宽高比（竖版 3:4）
const TARGET_ASPECT_RATIO = POLAROID_CONFIG.width / (POLAROID_CONFIG.height - POLAROID_CONFIG.bottomBorder)

// 调整纹理以实现 cover 效果（裁剪而非拉伸）
function adjustTextureForCover(texture: THREE.Texture, imageWidth: number, imageHeight: number) {
  const imageAspect = imageWidth / imageHeight
  
  if (imageAspect > TARGET_ASPECT_RATIO) {
    // 图片比目标更宽（横版图片）- 裁剪左右两边
    const scale = TARGET_ASPECT_RATIO / imageAspect
    texture.repeat.set(scale, 1)
    texture.offset.set((1 - scale) / 2, 0)
  } else if (imageAspect < TARGET_ASPECT_RATIO) {
    // 图片比目标更高（更窄的竖版）- 裁剪上下
    const scale = imageAspect / TARGET_ASPECT_RATIO
    texture.repeat.set(1, scale)
    texture.offset.set(0, (1 - scale) / 2)
  } else {
    // 宽高比完全匹配
    texture.repeat.set(1, 1)
    texture.offset.set(0, 0)
  }
  
  texture.needsUpdate = true
}

// 尝试加载照片纹理的Hook
function usePhotoTextures() {
  const textures = useMemo(() => {
    const loader = new THREE.TextureLoader()
    const loadedTextures: (THREE.Texture | null)[] = []
    
    PHOTO_PLACEHOLDERS.forEach((path, index) => {
      try {
        const texture = loader.load(
          path,
          // 成功回调
          (tex) => {
            tex.colorSpace = THREE.SRGBColorSpace
            
            // 获取图片原始尺寸并调整纹理
            if (tex.image) {
              const img = tex.image as HTMLImageElement
              adjustTextureForCover(tex, img.width, img.height)
            }
          },
          // 进度回调
          undefined,
          // 错误回调 - 静默处理，使用占位符颜色
          () => {
            console.log(`照片 ${index + 1} 未找到，使用占位符颜色。请将照片放入 public/photos/ 文件夹`)
          }
        )
        loadedTextures.push(texture)
      } catch {
        loadedTextures.push(null)
      }
    })
    
    return loadedTextures
  }, [])
  
  return textures
}

export function Polaroids({ progress, galleryProgress = 0 }: PolaroidsProps) {
  const groupRef = useRef<THREE.Group>(null)
  const textures = usePhotoTextures()
  const { focusPhotoIndex, setTargetState, setFocusPhotoIndex, resetSceneRotation } = useTreeStore()
  
  // 处理照片点击 - 进入画廊模式
  const handlePhotoClick = (imageIndex: number) => {
    // 设置焦点照片
    setFocusPhotoIndex(imageIndex)
    // 重置场景旋转
    resetSceneRotation()
    // 进入画廊模式
    setTargetState('GALLERY')
  }
  
  // 生成拍立得数据 - 增加数量到20张
  const polaroids = useMemo(() => {
    const polaroidCount = 20 // 增加照片数量到20张
    const data: PolaroidData[] = []
    
    // 使用确定性随机数生成器
    const seededRandom = (seed: number) => {
      const x = Math.sin(seed * 12.9898 + 78.233) * 43758.5453
      return x - Math.floor(x)
    }
    
    // 在树周围均匀分布照片
    for (let i = 0; i < polaroidCount; i++) {
      const angle = (i / polaroidCount) * Math.PI * 2
      const radius = 4 + seededRandom(i * 7) * 2.5 // 距离树中心4-6.5单位
      const height = 1.5 + (i % 5) * 2 // 分层放置，高度1.5-9.5
      
      const targetPos = new THREE.Vector3(
        Math.cos(angle) * radius,
        height,
        Math.sin(angle) * radius
      )
      
      // 混沌位置 - 在更大范围内随机分布
      const chaosPos = new THREE.Vector3(
        (Math.random() - 0.5) * 25,
        Math.random() * 18,
        (Math.random() - 0.5) * 25
      )
      
      // 旋转朝向树中心
      const rotation = new THREE.Euler(
        (Math.random() - 0.5) * 0.2,
        angle + Math.PI + (Math.random() - 0.5) * 0.3, // 朝向中心
        (Math.random() - 0.5) * 0.15
      )
      
      data.push({
        targetPosition: targetPos,
        chaosPosition: chaosPos,
        rotation,
        imageIndex: i % PHOTO_PLACEHOLDERS.length,
      })
    }
    
    return data
  }, [])
  
  // 计算画廊模式下每张照片的目标位置
  const getGalleryPosition = (_index: number, imageIndex: number) => {
    // 计算相对于焦点照片的偏移
    let offset = imageIndex - focusPhotoIndex
    const photoCount = PHOTO_PLACEHOLDERS.length
    
    // 处理循环
    if (offset > photoCount / 2) offset -= photoCount
    if (offset < -photoCount / 2) offset += photoCount
    
    // 中央照片（焦点）- 更靠近摄像机，位置更居中
    if (offset === 0) {
      return new THREE.Vector3(0, 4, 12) // z=12 更靠近摄像机
    }
    
    // 左右两侧的照片 - 调整位置使其更分散
    const side = offset > 0 ? 1 : -1
    const absOffset = Math.abs(offset)
    
    // 调整间距以适应更多照片
    const xPos = side * (4 + absOffset * 2.5) // 水平间距
    const zPos = 8 - absOffset * 1.5 // 深度
    const yPos = 4 - absOffset * 0.15
    
    return new THREE.Vector3(xPos, yPos, zPos)
  }
  
  // 计算当前位置（展开/成形状态）
  const getCurrentPosition = (data: PolaroidData) => {
    const weightedProgress = getWeightedProgress(progress, 0.5) // 中等权重
    return new THREE.Vector3().lerpVectors(
      data.chaosPosition,
      data.targetPosition,
      weightedProgress
    )
  }
  
  return (
    <group ref={groupRef}>
      {/* 画廊模式黑色背景 - 放在照片后面，z位置更远，renderOrder 最低 */}
      {galleryProgress > 0 && (
        <mesh position={[0, 4, -20]} renderOrder={-100}>
          <planeGeometry args={[200, 120]} />
          <meshBasicMaterial
            color="#000000"
            transparent
            opacity={galleryProgress * 0.95}
            depthWrite={false}
            depthTest={false}
            side={2} // DoubleSide
          />
        </mesh>
      )}
      
      {polaroids.map((polaroid, index) => {
        const isFocusPhoto = polaroid.imageIndex === focusPhotoIndex
        return (
          <PolaroidPhoto
            key={`polaroid-${index}`}
            position={getCurrentPosition(polaroid)}
            rotation={polaroid.rotation}
            progress={progress}
            galleryProgress={galleryProgress}
            galleryPosition={getGalleryPosition(index, polaroid.imageIndex)}
            imageIndex={polaroid.imageIndex}
            texture={textures[index % textures.length] || null}
            isFocusPhoto={isFocusPhoto}
            renderOrder={galleryProgress > 0 ? (isFocusPhoto ? 100 : 50) : 0}
            onPhotoClick={handlePhotoClick}
          />
        )
      })}
    </group>
  )
}